import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Modal,
  Fade,
  Backdrop
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useParams } from 'react-router-dom';

import CardShell from '../components/CardShell';
import { useClosurePayrollData } from '../hooks/useClosurePayrollData';
import { updateClosureEmployee, updateEmployeePayrollProfile, updatePayrollClosure, removeClosureEmployee } from '../api/liquidacionApi';
import { mapApiError } from '../../../../shared/utils/mapApiError';
import { fmtDMY, ymdToDate } from '../../../../shared/utils/dateFormat';

import ClosureEmployeeBalanceModal from '../components/ClosureEmployeeBalanceModal';
import ClosureEmployeeContractModal from '../components/ClosureEmployeeContractModal';
import { calculatePayrollSubtotal, formatMoney } from '../utils/moneyFormat';

function formatDateRange(periodStart, periodEnd) {
  if (!periodStart || !periodEnd) return '';
  const toDisplay = (val) => {
    if (!val) return '—';
    const d = val instanceof Date ? val : ymdToDate(String(val).slice(0, 10));
    return d ? fmtDMY(d) : '—';
  };
  return `${toDisplay(periodStart)} - ${toDisplay(periodEnd)}`;
}

function minutesToHours(minutes) {
  if (minutes == null) return '—';
  const h = Math.floor(Number(minutes) / 60);
  const m = Number(minutes) % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function parseBalanceConcepts(raw) {
  if (!raw || raw === '0.00' || raw === '[]' || raw === '') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function HeaderPill({ children, sx }) {
  return (
    <Box sx={{ px: 3, py: 1.2, bgcolor: 'secondary.50', borderRadius: '33px', boxShadow: 'inset 0px 0px 3px rgba(142, 142, 159, 0.5)', ...sx }}>
      {children}
    </Box>
  );
}

export function ColHeader({ children, sx }) {
  return (
    <Box sx={{ px: 0.8, py: 0.9, bgcolor: 'secondary.200', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0, ...sx }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'primary.900', textAlign: 'center', lineHeight: 1.1 }}>
        {children}
      </Typography>
    </Box>
  );
}

function ContractDisplayButton({ mode, onClick, isReady }) {
  const isHora = mode === 'HORA';
  const displayMode = isHora ? 'Hora' : 'Mensual';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: '8px',
        cursor: isReady ? 'default' : 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: isReady ? 'transparent' : 'neutral.200' },
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 11, color: 'primary.800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {displayMode}
      </Typography>
      {!isReady && <EditIcon sx={{ fontSize: 14, color: 'neutral.500', mb: '2px' }} />}
    </Box>
  );
}

function PayrollValueDisplay({ mode, baseSalary, hourlyRate }) {
  const value = mode === 'HORA' ? hourlyRate : baseSalary;
  return (
    <Typography sx={{ fontWeight: 800, fontSize: 11, color: 'primary.800', textAlign: 'center', whiteSpace: 'nowrap' }}>
      {formatMoney(value || 0)}
    </Typography>
  );
}

function BalanceDisplayButton({ value, onClick, isReady }) {
  const concepts = parseBalanceConcepts(value);
  const amount = concepts.reduce(
    (sum, concept) => (
      concept.type === 'descuento'
        ? sum - Number(concept.amount || 0)
        : sum + Number(concept.amount || 0)
    ),
    0,
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: '8px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: onClick ? 'neutral.200' : 'transparent' },
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 12, color: amount < 0 ? 'error.700' : 'success.700', whiteSpace: 'nowrap' }}>
        {formatMoney(amount)}
      </Typography>
      {isReady
        ? <VisibilityIcon sx={{ fontSize: 15, color: 'neutral.500' }} />
        : <EditIcon sx={{ fontSize: 15, color: 'neutral.500' }} />}
    </Box>
  );
}

function ObsInput({ value, onChange, onBlur, disabled }) {
  return (
    <TextField
      variant="outlined"
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      placeholder="Escribe aquí..."
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '20px', bgcolor: disabled ? 'neutral.100' : 'white', fontSize: 12,
          '& fieldset': { borderColor: disabled ? 'transparent' : 'secondary.300' },
        },
        minWidth: 0, width: '100%',
      }}
    />
  );
}

const GRID_COLS = 'minmax(150px, 1.5fr) 85px 110px 85px 85px 105px 105px 105px minmax(125px, 1.2fr)';
const GRID_GAP = 0.5;
const GRID_SIDE_PADDING = 0.5;
const SCROLLBAR_GUTTER = '16px';

function EmployeeRow({ employee, closureId, onPatchError, onSilentRefetch, onFullConsolidate, isReady }) {
  const [observations, setObservations] = useState(employee.observations ?? '');
  const [saving, setSaving] = useState(false);
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  useEffect(() => {
    setObservations(employee.observations ?? '');
  }, [employee.observations]);

  const handlePatch = useCallback(async (fields) => {
    setSaving(true);
    try {
      await updateClosureEmployee(closureId, employee.employeeId, fields);
    } catch (err) {
      onPatchError(mapApiError(err).message || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  }, [closureId, employee.employeeId, onPatchError]);

  const handleSaveBalance = async (newConcepts) => {
    const newBalanceJson = JSON.stringify(newConcepts);

    setSaving(true);
    try {
      await updateClosureEmployee(closureId, employee.employeeId, {
        balanceAmount: newBalanceJson,
      });

      setBalanceModalOpen(false);
      onSilentRefetch();
    } catch (err) {
      onPatchError(mapApiError(err).message || 'Error al actualizar balance');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContract = async (payload) => {
    setSaving(true);
    try {
      await updateEmployeePayrollProfile(employee.employeeId, payload);
      setContractModalOpen(false);
      onFullConsolidate();
    } catch (err) {
      onPatchError(mapApiError(err).message || 'Error al actualizar contrato');
    } finally {
      setSaving(false);
    }
  };

  const isZeroHours = Number(employee.workedMinutes) === 0;
  const subtotalAmount = calculatePayrollSubtotal(employee);
  const subtotalDisplay = formatMoney(subtotalAmount);
  const totalDisplay = formatMoney(employee.totalAmount);
  const totalPositive = Number(employee.totalAmount ?? 0) >= 0;

  return (
    <>
      <Box
        sx={{
          display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center', gap: GRID_GAP, px: GRID_SIDE_PADDING, py: 1.2,
          bgcolor: isZeroHours ? '#ef9a9a' : 'neutral.50', 
          borderRadius: '16px', border: '1px solid', 
          borderColor: 'neutral.200',
          opacity: saving ? 0.7 : 1, transition: 'all 0.15s ease-in-out',
          '&:hover': { 
            bgcolor: isZeroHours ? '#ef9a9a' : 'neutral.100', 
            borderColor:'neutral.300' 
          },
          '& > *': { minWidth: 0 },
          mb: 1,
          pl: 1
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
          {employee.fullName || '—'}
        </Typography>

        <ContractDisplayButton
          mode={employee.salaryMode}
          onClick={saving || isReady ? undefined : () => setContractModalOpen(true)}
          isReady={isReady}
        />

        <PayrollValueDisplay
          mode={employee.salaryMode}
          baseSalary={employee.baseSalary}
          hourlyRate={employee.hourlyRate}
        />

        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.800', textAlign: 'center', whiteSpace: 'nowrap' }}>{minutesToHours(employee.assignedMinutes)}</Typography>
        
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: isZeroHours ? '#c62828' : 'primary.800', textAlign: 'center', whiteSpace: 'nowrap' }}>
            {minutesToHours(employee.workedMinutes)}
        </Typography>

        <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'primary.800', textAlign: 'center', whiteSpace: 'nowrap' }}>
          {subtotalDisplay}
        </Typography>

        <BalanceDisplayButton
          value={employee.balanceAmount ?? '0'}
          onClick={saving ? undefined : () => setBalanceModalOpen(true)}
          isReady={isReady}
        />

        <Box sx={{ px: 1, py: 0.6, bgcolor: totalPositive ? 'success.50' : 'error.50', borderRadius: '20px', textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: totalPositive ? 'success.800' : 'error.800', whiteSpace: 'nowrap' }}>
            {totalDisplay}
          </Typography>
        </Box>

        <ObsInput
          position="right"
          value={observations}
          onChange={setObservations}
          onBlur={() => handlePatch({ observations })}
          disabled={saving || isReady}
        />
      </Box>

      <ClosureEmployeeBalanceModal
        open={balanceModalOpen}
        onClose={saving ? undefined : () => setBalanceModalOpen(false)}
        initialConcepts={parseBalanceConcepts(employee.balanceAmount ?? '[]')}
        fullName={employee.fullName}
        onSave={handleSaveBalance}
        saving={saving}
        readOnly={isReady}
      />

      <ClosureEmployeeContractModal
        open={contractModalOpen}
        onClose={saving ? undefined : () => setContractModalOpen(false)}
        employee={employee}
        onSave={handleSaveContract}
        saving={saving}
      />
    </>
  );
}

export default function ClosurePayrollDataPage() {
  const navigate = useNavigate();
  const { closureId } = useParams();

  const { closure, employees, loading, error, refetchList, refetch } = useClosurePayrollData(closureId);
  const [patchError, setPatchError] = useState('');
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [zeroModalOpen, setZeroModalOpen] = useState(false);
  const [zeroEmployees, setZeroEmployees] = useState([]);

  if (loading || !closure || closure.id !== closureId){
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isReady = closure?.status === 'READY';

  const handleBack = () => {
    if (isReady) {
      navigate('/rrhh/liquidacion');
    } else {
      navigate(`/rrhh/liquidacion/${closureId}/usuarios`);
    }
  };

  const handleContinueClick = () => {
    if (!isReady) {
      const zeroes = employees.filter(emp => Number(emp.totalAmount) === 0);
      if (zeroes.length > 0) {
        setZeroEmployees(zeroes);
        setZeroModalOpen(true);
      } else {
        executeAdvancement([]);
      }
    } else {
      navigate(`/rrhh/liquidacion/${closureId}/accounting`);
    }
  };

  const executeAdvancement = async (employeesToRemove = []) => {
    setIsAdvancing(true);
    try {
      if (employeesToRemove.length > 0) {
        for (const emp of employeesToRemove) {
          await removeClosureEmployee(closureId, emp.employeeId);
        }
      }
      await updatePayrollClosure(closureId, { currentStep: 4 });
      setZeroModalOpen(false); 
      navigate(`/rrhh/liquidacion/${closureId}/accounting`);
    } catch (err) {
      setPatchError(mapApiError(err).message || "Error al excluir empleados.");
      setIsAdvancing(false);
      setZeroModalOpen(false);
    }
  };

  if (loading && employees.length === 0) {
    return (
      <Stack spacing={2} sx={{ p: 2, alignItems: 'center', py: 8 }}>
        <CircularProgress size={40} color="primary" />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'primary.900' }}>Calculando liquidación...</Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} sx={{ alignSelf: 'flex-start' }}>Volver</Button>
      </Stack>
    );
  }

  const dateRange = closure ? formatDateRange(closure.periodStart, closure.periodEnd) : '';

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <IconButton onClick={() => navigate('/rrhh/liquidacion')} sx={{ width: 40, height: 40, bgcolor: 'secondary.500', '&:hover': { bgcolor: 'secondary.600' } }}>
          <ArrowBackIcon sx={{ color: 'logoblanco', fontSize: 32 }} />
        </IconButton>

        <HeaderPill sx={{ bgcolor: 'secondary.200' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900' }}>
            {isReady ? `Detalle Liquidacion: ${closure?.name || ''}` : 'Liquidación de Sueldo'}
          </Typography>
        </HeaderPill>

        {dateRange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'secondary.50', borderRadius: '20px', border: '1.5px solid', borderColor: 'secondary.200' }}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'primary.700' }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900' }}>{dateRange}</Typography>
          </Box>
        )}
      </Stack>

      {patchError && <Alert severity="error" onClose={() => setPatchError('')}>{patchError}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#A9A9B6", borderRadius: "24px", p: 2, overflow: "hidden" }}>
        <CardShell sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              mr: `-${SCROLLBAR_GUTTER}`,
              pr: 2,
            }}
          >
            <Box sx={{ minWidth: 1080 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, gap: GRID_GAP, px: GRID_SIDE_PADDING, pb: 1.5, flexShrink: 0, '& > *': { minWidth: 0 } }}>
                <ColHeader>Nombre y apellido</ColHeader>
                <ColHeader>Modalidad</ColHeader>
                <ColHeader>Valor aplicado</ColHeader>
                <ColHeader>Hs Asignadas</ColHeader>
                <ColHeader>Hs Trabajadas</ColHeader>
                <ColHeader>Subtotal</ColHeader>
                <ColHeader>Balance</ColHeader>
                <ColHeader>Total</ColHeader>
                <ColHeader>Observaciones</ColHeader>
              </Box>

              {employees.length === 0 ? (
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'neutral.700', textAlign: 'center', py: 4 }}>
                  No hay empleados en este cierre
                </Typography>
              ) : (
                employees.map((emp) => (
                  <EmployeeRow
                    key={emp.employeeId}
                    employee={emp}
                    closureId={closureId}
                    onPatchError={setPatchError}
                    onSilentRefetch={refetchList}
                    onFullConsolidate={refetch}
                    isReady={isReady}
                  />
                ))
              )}
            </Box>
          </Box>
        </CardShell>
      </Box>

      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 2, flexShrink: 0 }}>
        <Button 
          onClick={handleContinueClick} 
          disabled={employees.length === 0 || isAdvancing} 
          sx={{ bgcolor: "#b9f6ca", color: "primary.900", px: 3, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, '&:hover': { bgcolor: "#a5f5bd" }, '&:disabled': { opacity: 0.6 } }}
        >
          {isAdvancing ? <CircularProgress size={20} color="inherit" /> : 'Continuar'}
        </Button>
      </Stack>

      {/*MODAL PERSONALIZADO PARA EMPLEADOS EN 0*/}
      <Modal
        open={zeroModalOpen}
        onClose={isAdvancing ? undefined : () => setZeroModalOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 400 } }}
      >
        <Fade in={zeroModalOpen}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 450 }, bgcolor: 'neutral.50', borderRadius: '24px', 
            boxShadow: 24, p: 4, outline: 'none', border: '2px solid', borderColor: 'primary.900'
          }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: '50%', mb: 1 }}>
                <WarningAmberIcon sx={{ fontSize: 48, color: '#f57c00' }} />
              </Box>
              
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.900' }}>
                Atención: Empleados en $0.00
              </Typography>
              
              <Typography sx={{ fontSize: 15, color: 'neutral.700', lineHeight: 1.5 }}>
                Hemos detectado <b>{zeroEmployees.length} empleado(s)</b> con un total a liquidar de $0.00. 
                Si continúas, serán excluidos automáticamente de este cierre y no se les generará recibo de sueldo.
              </Typography>

              <Stack direction="column" spacing={1.5} sx={{ width: '100%', mt: 3 }}>
                <Button
                  fullWidth
                  disabled={isAdvancing}
                  onClick={() => executeAdvancement(zeroEmployees)}
                  sx={{ bgcolor: "#b9f6ca", color: "primary.900", py: 1.2, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 15, '&:hover': { bgcolor: "#a5f5bd" } }}
                >
                  {isAdvancing ? <CircularProgress size={24} color="inherit" /> : 'Confirmar y excluir'}
                </Button>
                
                <Button
                  fullWidth
                  disabled={isAdvancing}
                  onClick={() => executeAdvancement([])} // Mandamos array vacío para no borrar nada
                  sx={{ bgcolor: "#ffe082", color: "primary.900", py: 1.2, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 15, '&:hover': { bgcolor: "#ffd54f" } }}
                >
                  {isAdvancing ? <CircularProgress size={24} color="inherit" /> : 'Continuar sin excluir (Mantener en $0)'}
                </Button>

                <Button
                  fullWidth
                  disabled={isAdvancing}
                  onClick={() => setZeroModalOpen(false)}
                  sx={{ bgcolor: "#ffcdd2", color: "primary.900", py: 1.2, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 15, '&:hover': { bgcolor: "#ffb3ba" } }}
                >
                  Revisar datos
                </Button>
              </Stack>

            </Stack>
          </Box>
        </Fade>
      </Modal>

    </Box>
  );
}
